import { Header } from 'components/Layout/Header'
import {
  LeftNavBar,
  NavItemsProvider,
  SlideOutPanel,
  useLeftNavStore,
} from 'components/Layout/LeftNav'
import { cn } from 'components/UI/cn'
import { UnifiedSearchModal } from 'components/UnifiedSearch'
import { useAuth } from 'context/authentication/useAuth'
import { LayoutConfigProvider } from 'context/layout'
import { useCallback } from 'react'
import { Outlet, useLocation, useSearchParams } from 'react-router-dom'

export function AppLayout() {
  return (
    <LayoutConfigProvider>
      <NavItemsProvider>
        <AppLayoutContent />
      </NavItemsProvider>
    </LayoutConfigProvider>
  )
}

function AppLayoutContent() {
  const { isAuthenticated } = useAuth()
  const { isCollapsed, isSlideOutOpen, activeSection } = useLeftNavStore()
  const [, setSearchParams] = useSearchParams()
  const location = useLocation()

  const openSearch = useCallback(() => {
    setSearchParams((prev) => {
      prev.set('search', 'true')
      return prev
    })
  }, [setSearchParams])

  // Settings is no longer in the left nav, so the slide-out has no anchor
  // button to re-open it. Treat the panel as persistent on /settings so it
  // renders open even on a fresh URL hit before the store updates.
  const isSettingsRoute = location.pathname.startsWith('/settings')
  // The /settings auto-open (SlideOutPanel) writes activeSection='settings' +
  // isSlideOutOpen into the store, and nothing clears it when you leave via a
  // route that has no panel of its own (e.g. Deals). Without excluding it here,
  // that stale 'settings' state keeps the panel rendered on the new page. The
  // settings panel is route-pinned, so only show it on /settings — never from
  // the transient, store-driven branch.
  const showSlideOut =
    isAuthenticated &&
    (isSettingsRoute ||
      (isSlideOutOpen && !!activeSection && activeSection !== 'settings'))

  const hasLeftNav = isAuthenticated && !isCollapsed

  return (
    <div className="flex flex-col h-screen w-full overflow-hidden bg-[var(--nav-bg-primary)]">
      {/* Top Header - spans full width */}
      {isAuthenticated && <Header onSearchClick={openSearch} />}

      {/* Main Area: Left Nav + Content */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Navigation Bar */}
        {isAuthenticated && <LeftNavBar />}

        {/* App Shell with border effect */}
        <div
          className={cn(
            'flex flex-row flex-1 min-w-0 mx-[var(--app-border-width)] mb-[var(--app-border-width)] mt-1 bg-surface-sunken rounded-xl overflow-hidden',
            hasLeftNav ? 'ml-0' : 'ml-[var(--app-border-width)]'
          )}
        >
          {/* Slide-Out Panel - inside AppShell, pushes content */}
          {isAuthenticated && <SlideOutPanel $isOpen={!!showSlideOut} />}

          <div className="flex flex-col flex-1 min-w-0">
            <div
              id="app-content-container"
              // scrollbar-gutter:stable reserves the scrollbar's space whether
              // or not content overflows, so centered (mx-auto) pages don't
              // shift horizontally when their height crosses the scroll
              // threshold (e.g. toggling a report between chart and table).
              className="flex-1 flex flex-col overflow-auto [scrollbar-gutter:stable]"
            >
              <Outlet />
            </div>
          </div>
        </div>
      </div>

      {isAuthenticated && <UnifiedSearchModal />}
    </div>
  )
}
